import React from 'react'
import { Platform } from 'react-native'

import { FormattedMessage } from 'react-intl'
import { useRouter } from 'solito/router'

import Button from 'app/components/base/Button'
import Stack, { StackProps } from 'app/components/base/Stack'
import AddToCartButton from 'app/features/ListingFeature/components/AddToCartButton'
import MakeOfferButton from 'app/features/ListingFeature/components/MakeOfferButton'
import QuantityController from 'app/features/ListingFeature/QuantityController'
import useCurrencyState from 'app/hooks/useCurrencyState'
import {
  getAddToCartEventParams,
  useSendGoogleAnalyticsEvent
} from 'app/hooks/useSendGoogleAnalyticsEvent'
import useAppStore from 'app/store/app.store'
import useAuthStore, { getAuthUser } from 'app/store/auth.store'
import { defaultCurrency } from 'app/utils/constants/intl.constants'
import { hasGuestStatus } from 'app/utils/helpers/user.helpers'

import Price from '../components/Price'
import SimilarItems from '../SimilarItems'

import AuctionActions from './components/AuctionActions'

interface ShowActionsProps {
  actualPrice?: number
  convertedActualPrice?: number
  auctionId?: number
  bidsCount?: number
  auctionReserveStatus?: string
  sx?: StackProps['sx']
  disabled?: boolean
  acceptOffers?: boolean
  isAuthUserListing?: boolean
  slug: string
  listingCurrency?: Currency
  inWatchList?: boolean
  watchListId?: string
  exchangeRate?: number
  availableQuantity?: number
  soldQuantity?: number
  reservedQuantity?: number
  isAuction: boolean
  listingId?: string
  hideQuantity?: boolean
  isSold?: boolean
  isHighestBidder?: boolean
  isOutbid?: boolean
  maxBid?: MaxBidModel
  convertedTradePrice?: number
  tradePrice?: number
  showSimilarItems?: boolean
  similarItems?: ListingModel[]
  isLargeSize?: boolean
  listingTitle?: string
}

const ShowActions: RFC<ShowActionsProps> = ({
  actualPrice,
  convertedActualPrice,
  auctionId = 0,
  bidsCount = 0,
  auctionReserveStatus,
  disabled = false,
  acceptOffers = true,
  isAuthUserListing,
  listingCurrency,
  slug,
  inWatchList,
  watchListId,
  exchangeRate,
  availableQuantity = 0,
  soldQuantity = 0,
  reservedQuantity,
  listingId = '',
  isAuction,
  hideQuantity,
  isSold,
  isHighestBidder,
  isOutbid,
  maxBid,
  sx,
  convertedTradePrice,
  tradePrice,
  showSimilarItems,
  similarItems,
  isLargeSize,
  listingTitle
}) => {
  const { push } = useRouter()
  const { currency } = useCurrencyState()
  const isTablet = useAppStore((state) => state.isTablet)
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const user = useAuthStore(getAuthUser)
  const isGuestUser = hasGuestStatus(user?.status)
  const userId = isGuestUser ? undefined : user?.id

  const disabledButton = disabled || !availableQuantity
  const availableQuantitySum = availableQuantity - soldQuantity

  const showAuctionActions = !isAuthUserListing && isAuction
  const showAddToCartButton = !isAuthUserListing && !isAuction
  const hasSimilarItems = !!similarItems?.length && similarItems?.length > 0

  const sendGAEvent = (cartQuantity: string | number) => {
    if (!listingTitle || !listingId) return

    const ecommerceParams = getAddToCartEventParams({
      listingId,
      listingTitle,
      cartQuantity,
      actualPrice
    })

    sendGA4Event({
      eventName: 'add_to_cart',
      ...ecommerceParams,
      user_id: userId
    })
  }

  return (
    <Stack
      spacing={{ md: isTablet ? 2 : 3, xs: 2 }}
      sx={{
        py: { md: isTablet ? 3 : 1, xs: 3 },
        px: { md: isTablet ? 2 : 0, xs: 2 },
        width: '100%',
        borderTopColor: 'stroke.main',
        borderTopWidth: { md: isTablet ? 1 : 0, xs: 1 },
        alignItems: { xs: 'center', md: isTablet ? 'center' : 'flex-start' },
        zIndex: 100,
        bottom: 0,
        left: 0,
        backgroundColor: 'background.default',
        ...Platform.select({
          web: {
            position: { xs: 'fixed', md: 'static' },
            borderTopStyle: 'solid',
            zIndex: 1
          }
        }),
        ...sx
      }}
    >
      {!showSimilarItems && (
        <>
          <Price
            actualPrice={actualPrice}
            convertedActualPrice={convertedActualPrice}
            isAuction={isAuction}
            userCurrency={currency}
            listingCurrency={listingCurrency}
            isCurrentBid={!!bidsCount}
            isHighestBidder={isHighestBidder}
            isOutbid={isOutbid}
            convertedTradePrice={convertedTradePrice}
            tradePrice={tradePrice}
          />

          <QuantityController
            availableQuantity={availableQuantity}
            reservedQuantity={reservedQuantity}
            soldQuantity={soldQuantity}
            hideQuantity={hideQuantity}
            isOffer={acceptOffers}
            isSold={isSold}
            sx={{ display: { xs: 'none', md: 'flex' } }}
          />

          {isAuthUserListing && (
            <Stack
              direction="row"
              sx={{
                width: '100%',
                justifyContent: {
                  xs: 'center',
                  md: isTablet ? 'center' : 'flex-start'
                }
              }}
            >
              <Button
                onClick={() => push(`/profile/listing/edit/${slug}`)}
                disabled={disabled}
                sx={{
                  flex: 1,
                  px: 0,
                  ...Platform.select({
                    web: {
                      maxWidth: 165
                    },
                    native: {
                      maxWidth: '100%'
                    }
                  })
                }}
              >
                <FormattedMessage defaultMessage="Manage Listing" />
              </Button>
            </Stack>
          )}

          {showAuctionActions && (
            <AuctionActions
              auctionId={auctionId}
              bidsCount={bidsCount}
              auctionReserveStatus={auctionReserveStatus}
              currency={listingCurrency?.name || defaultCurrency}
              disabled={disabled}
              inWatchList={inWatchList}
              watchListId={watchListId}
              exchangeRate={exchangeRate}
              maxBid={maxBid}
            />
          )}

          {showAddToCartButton && (
            <Stack
              direction="row"
              spacing={1.5}
              sx={{
                width: '100%',
                justifyContent: {
                  xs: 'center',
                  md: isTablet ? 'center' : 'flex-start'
                }
              }}
            >
              {acceptOffers && (
                <MakeOfferButton
                  buttonProps={{
                    sx: {
                      maxWidth: { xs: 'auto', md: isTablet ? 300 : 165 },
                      borderWidth: disabledButton ? 0 : 0.3
                    }
                  }}
                  disabled={disabledButton}
                  listingId={listingId}
                  listingCurrency={listingCurrency?.name || defaultCurrency}
                  availableQuantity={availableQuantity}
                />
              )}

              <AddToCartButton
                slug={slug}
                disabled={disabledButton}
                availableQuantitySum={availableQuantitySum}
                sendGAEvent={sendGAEvent}
              />
            </Stack>
          )}
        </>
      )}

      {showSimilarItems && hasSimilarItems && (
        <SimilarItems similarItems={similarItems} isLargeSize={isLargeSize} />
      )}
    </Stack>
  )
}

export default ShowActions
